// src/app/services/translation.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type TranslationsMap = Record<string, any>;

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private translations: TranslationsMap = {};
  private currentLang = 'es';
  private isBrowser: boolean;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /** 
   * Sólo se ejecuta en el navegador. 
   * En SSR sale inmediatamente con el idioma por defecto. 
   */
  async initialize(): Promise<void> {
    if (!this.isBrowser) {
      this.currentLang = 'es';
      return;
    }

    // Lógica de detección de idioma (localStorage o navegador)
    const stored = localStorage.getItem('lang');
    const browserLang = navigator.language.split('-')[0];
    const lang = stored
      ?? (['es','en'].includes(browserLang) ? browserLang : 'es');

    this.currentLang = lang;
    localStorage.setItem('lang', lang);

    // Y ahora SÍ pedimos el JSON
    await this.loadTranslations(lang);
  }

  private async loadTranslations(lang: string): Promise<void> {
    try {
      this.translations = await firstValueFrom(
        this.http.get<TranslationsMap>(`/assets/i18n/${lang}.json`)
      ) || {};
    } catch (err) {
      console.error('❌ Error cargando traducciones:', err);
      this.translations = {};
    }
  }

  translate(key: string): string {
    const parts = key.split('.');
    let result: any = this.translations;
    for (const part of parts) {
      if (result && part in result) {
        result = result[part];
      } else {
        console.warn(`❌ Clave no encontrada: ${key}`);
        return key;
      }
    }
    return typeof result === 'string' ? result : key;
  }

  async changeLanguage(lang: string): Promise<void> {
    if (!this.isBrowser || lang === this.currentLang) return;
    localStorage.setItem('lang', lang);
    this.currentLang = lang;
    await this.loadTranslations(lang);
  }

  get currentLanguage(): string {
    return this.currentLang;
  }
}
